import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, MapPin, Play, Lightbulb, Image as ImageIcon, 
  Video, ChevronLeft, ChevronRight, X, Globe, Ship, Map, Compass
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CITY_NAMES = {
  playa_del_carmen: "Playa del Carmen",
  cancun: "Cancún", 
  tulum: "Tulum",
  los_cabos: "Los Cabos"
};

const CATEGORY_ICONS = {
  yacht: Ship,
  tour: Map,
  activity: Compass
};

export default function CityDetailPage() {
  const { cityId } = useParams();
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const { user } = useAuth();
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/city-content/${cityId}`);
        setContent(res.data);
      } catch (error) {
        console.error('Error fetching city content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [cityId]);

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    if (content?.gallery_images) {
      setLightboxIndex((prev) => (prev + 1) % content.gallery_images.length);
    }
  };

  const prevImage = () => {
    if (content?.gallery_images) {
      setLightboxIndex((prev) => (prev - 1 + content.gallery_images.length) % content.gallery_images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  const cityName = CITY_NAMES[cityId] || cityId;
  const description = language === 'es' ? content?.description_es : content?.description_en;
  const tips = language === 'es' ? content?.tips_es : content?.tips_en;

  return (
    <div className="min-h-screen bg-card" data-testid="city-detail-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl font-bold text-foreground">
            Bid<span className="text-caribbean">Trip</span>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Globe size={18} />
              <span>{language === 'en' ? 'ES' : 'EN'}</span>
            </button>
            {user ? (
              <Link to="/dashboard">
                <Button className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white">
                  {language === 'en' ? 'Login' : 'Iniciar Sesión'}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section with Cover Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        {content?.cover_image ? (
          <img 
            src={content.cover_image} 
            alt={cityName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-caribbean to-caribbean/60" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-24 left-4 md:left-8 bg-white/20 backdrop-blur-md rounded-full p-2 hover:bg-white/30 transition-colors"
        >
          <ArrowLeft className="text-white" size={24} />
        </Link>

        {/* City Name */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 text-white/80 mb-2">
              <MapPin size={18} />
              <span className="text-sm">
                {language === 'en' ? 'Explore' : 'Explora'}
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white">
              {cityName}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Description */}
        {description && (
          <section className="mb-12">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
              {description}
            </p>
          </section>
        )}

        {/* Tips Section */}
        {tips && tips.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gold/20 rounded-xl">
                <Lightbulb className="text-gold" size={24} />
              </div>
              <h2 className="font-heading text-2xl font-bold">
                {language === 'en' ? 'Travel Tips' : 'Tips de Viaje'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tips.map((tip, index) => (
                <div 
                  key={index}
                  className="bg-gold/5 border border-gold/20 rounded-xl p-4 flex items-start gap-3"
                >
                  <span className="bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {index + 1}
                  </span>
                  <p className="text-muted-foreground">{tip}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Gallery Section */}
        {content?.gallery_images && content.gallery_images.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-caribbean/20 rounded-xl">
                <ImageIcon className="text-caribbean" size={24} />
              </div>
              <h2 className="font-heading text-2xl font-bold">
                {language === 'en' ? 'Gallery' : 'Galería'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {content.gallery_images.map((img, index) => (
                <div 
                  key={index}
                  className="aspect-square rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => openLightbox(index)}
                >
                  <img 
                    src={img} 
                    alt={`${cityName} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Videos Section */}
        {content?.videos && content.videos.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-rosa/20 rounded-xl">
                <Video className="text-rosa" size={24} />
              </div>
              <h2 className="font-heading text-2xl font-bold">
                {language === 'en' ? 'Videos' : 'Videos'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.videos.map((video, index) => (
                <div key={index} className="rounded-xl overflow-hidden bg-muted">
                  {activeVideo === index ? (
                    <div className="aspect-video">
                      <iframe
                        src={getVideoEmbedUrl(video.url)}
                        title={video.title || `Video ${index + 1}`}
                        className="w-full h-full"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  ) : (
                    <div 
                      className="aspect-video relative cursor-pointer group"
                      onClick={() => setActiveVideo(index)}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-caribbean/80 to-rosa/80 flex items-center justify-center">
                        <div className="bg-card rounded-full p-4 group-hover:scale-110 transition-transform">
                          <Play className="text-caribbean" size={32} fill="currentColor" />
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-white font-semibold">{video.title || `Video ${index + 1}`}</p>
                        <Badge variant="secondary" className="mt-1">
                          {video.type || 'YouTube'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Activities Section */}
        {content?.activities && content.activities.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Compass className="text-purple-600" size={24} />
              </div>
              <h2 className="font-heading text-2xl font-bold">
                {language === 'en' ? 'Things to Do' : 'Qué Hacer'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.activities.map((activity, index) => {
                const IconComponent = CATEGORY_ICONS[activity.category] || Compass;
                return (
                  <div 
                    key={index}
                    className="rounded-xl overflow-hidden border shadow-lg hover:shadow-xl transition-shadow"
                  >
                    {activity.image_url && (
                      <div className="aspect-video relative">
                        <img 
                          src={activity.image_url}
                          alt={language === 'es' ? activity.title_es : activity.title_en}
                          className="w-full h-full object-cover"
                        />
                        {activity.video_url && (
                          <button 
                            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                            onClick={() => {
                              setActiveVideo(`activity-${index}`);
                            }}
                          >
                            <div className="bg-card rounded-full p-3">
                              <Play className="text-caribbean" size={24} fill="currentColor" />
                            </div>
                          </button>
                        )}
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent size={18} className="text-caribbean" />
                        <Badge variant="outline" className="text-xs">
                          {activity.category || 'activity'}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-lg">
                        {language === 'es' ? activity.title_es : activity.title_en}
                      </h3>
                      {activity.description && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {language === 'es' ? activity.description_es : activity.description_en}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* CTA Button */}
        <section className="text-center py-12 border-t">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            {language === 'en' 
              ? `Ready to explore ${cityName}?` 
              : `¿Listo para explorar ${cityName}?`}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {language === 'en'
              ? 'Create a request and receive offers from local providers'
              : 'Crea una solicitud y recibe ofertas de proveedores locales'}
          </p>
          <Link to={`/create-request?location=${cityId}`}>
            <Button 
              size="lg" 
              className="bg-caribbean hover:bg-caribbean/90 text-white rounded-full px-8 py-6 text-lg"
              data-testid="request-experience-btn"
            >
              {language === 'en' ? 'Request an Experience' : 'Solicitar Experiencia'}
            </Button>
          </Link>
        </section>
      </main>

      {/* Lightbox */}
      {lightboxOpen && content?.gallery_images && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-white/80"
            onClick={() => setLightboxOpen(false)}
          >
            <X size={32} />
          </button>
          
          <button 
            className="absolute left-4 text-white hover:text-white/80 p-2"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft size={48} />
          </button>
          
          <img 
            src={content.gallery_images[lightboxIndex]}
            alt={`${cityName} ${lightboxIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button 
            className="absolute right-4 text-white hover:text-white/80 p-2"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight size={48} />
          </button>
          
          <div className="absolute bottom-4 text-white text-sm">
            {lightboxIndex + 1} / {content.gallery_images.length}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 border-t bg-muted/30">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-heading text-xl font-bold">
            Bid<span className="text-caribbean">Trip</span>
          </Link>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <Link to="/terms" className="hover:text-caribbean">
              {language === 'en' ? 'Terms' : 'Términos'}
            </Link>
            <Link to="/privacy" className="hover:text-caribbean">
              {language === 'en' ? 'Privacy' : 'Privacidad'}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
